import type { z } from 'zod';
import type { Category } from '../../entities/Category';
import { getCatalogTypeFromDto } from '../../utils/catalog/catalog';
import {
  STATE_INACTIVE,
  STATE_UNPUBLISHED,
  STATE_VERIFIED,
} from '../../utils/category/states';
import {
  CATALOG_STATUS_DRAFT_ACTIVATION_IN_PROGRESS,
  CATALOG_STATUS_DRAFT_CREATE_IN_PROGRESS,
} from '../../utils/statuses/catalogStatus';
import { CATALOG_TYPE_AUTOMATED } from '../../utils/types/catalogTypes';
import type { CategorySchema } from '../../schemas/categories';
import type { Catalog } from '../../entities/Catalog';
import type { ItemSchema } from '../../schemas/items';
import type { CatalogSchema, DraftCatalogSchema } from '../../schemas/catalogs';

export function catalogFromDto({
  catalogueId,
  catalogueIdentifierTranslations,
  catalogueName,
  catalogueStatus,
  catalogueType,
  globalCatalogueIdentifier,
  ...catalog
}: z.infer<typeof CatalogSchema>): Catalog {
  const catalogName =
    catalogueType === CATALOG_TYPE_AUTOMATED
      ? `${catalogueName} (AB Test)`
      : catalogueName;
  return {
    ...catalog,
    catalogId: `${catalogueId}`,
    catalogIdentifierTranslations: catalogueIdentifierTranslations,
    catalogName,
    catalogStatus: catalogueStatus,
    catalogType: getCatalogTypeFromDto({ catalogueType }),
    globalCatalogIdentifier: globalCatalogueIdentifier,
  };
}

export function catalogStatusFromDto({
  catalogueId,
  status,
  hasDraft,
}: z.infer<typeof DraftCatalogSchema>) {
  return {
    catalogId: `${catalogueId}`,
    isProcessingDraftCatalog: [
      CATALOG_STATUS_DRAFT_CREATE_IN_PROGRESS,
      CATALOG_STATUS_DRAFT_ACTIVATION_IN_PROGRESS,
    ].includes(status || ''),
    status,
    hasDraft,
  };
}

export function catalogToDto({
  catalogId,
  catalogIdentifierTranslations,
  catalogName,
  catalogStatus,
  catalogType,
  globalCatalogIdentifier,
  ...catalog
}: Catalog) {
  return {
    catalogueId: Number(catalogId),
    catalogueIdentifierTranslations: catalogIdentifierTranslations,
    catalogueName: catalogName,
    catalogueStatus: catalogStatus,
    catalogueType: catalogType,
    globalCatalogueIdentifier: globalCatalogIdentifier,
    ...catalog,
  };
}

export function categoryFromDto(category: z.infer<typeof CategorySchema>) {
  const dtoFromCategory = category as Category;
  dtoFromCategory.categoryNameTranslationApproved =
    dtoFromCategory.translateState === STATE_VERIFIED;
  dtoFromCategory.published = dtoFromCategory.publishState !== STATE_UNPUBLISHED;
  dtoFromCategory.shopActive = dtoFromCategory.shopActiveStatus
    ? dtoFromCategory.shopActiveStatus !== STATE_INACTIVE
    : false;

  return dtoFromCategory;
}

export function categoryToDto({
  autoCategorisable,
  categoryComment,
  categoryId,
  categoryNameTranslations,
  categoryNameTranslationApproved,
  externalCategoryId,
  globalCategoryComment,
  globalCategoryName,
  globalCategoryPublished,
  globalPublishDate,
  published,
  mainParentCategoryId,
  categoryType,
  roomsSlug,
  image,
  imageComment,
  shopActiveStatus,
  markForDeletion,
  redirectedCategoryId,
  overrideShopTopicAlert,
}: Category) {
  return {
    autoCategorisable,
    categoryComment,
    categoryId: Number(categoryId),
    categoryNameTranslations,
    categoryNameTranslationApproved,
    externalCategoryId,
    globalCategoryComment,
    globalCategoryName,
    globalCategoryPublished,
    globalPublishDate,
    mainParentCategoryId,
    published,
    categoryType,
    roomsSlug,
    image,
    imageComment,
    shopActiveStatus,
    markForDeletion,
    redirectedCategoryId: redirectedCategoryId ? redirectedCategoryId : null,
    overrideShopTopicAlert,
  };
}
export type ItemDTO = ReturnType<typeof itemFromDto>;

export function itemFromDto(itemDTO: z.infer<typeof ItemSchema>) {
  return itemDTO;
}
